<template>
  <div :id="`voice-sdk-container-${conversationId}`"></div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import { EventBus } from "@/events/eventBus";

export default {
  name: 'TranscribeSDK',
  props: {
    conversationId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      liveButton: null,
      audioContext: null,
      audioNode: null,
      gainNode: null,
      hasLiveAudio: false,
      liveAudioCapability: false,
      playingAudio: false,
    }
  },
  computed: {
    ...mapGetters("user", ["isVoiceSDKLoaded", "volume"]),
    ...mapGetters("agency", ["agencySettings"]),
    ...mapGetters("conversations", ["currentConversation"]),
  },
  created() {
    this.$store.commit('keywords/clearKeywords');
    this.hasLiveAudio = false;
    this.liveButton = null;
    if (
      this.currentConversation.channel === 'voice' &&
      this.currentConversation.status === 'active' &&
      this.$route.name === 'Conversation Detail'
    ) {
      this.liveAudioCapability = true;
    }
    if (this.$conveyInstance) {
      this.$nextTick(() => {
        const params = {
          correlationId: this.currentConversation.primary_recipient,
          conversationId: this.conversationId,
          $container: `voice-sdk-container-${this.conversationId}`,
          $summaryContainer: `conversationSummary-${this.conversationId}`,
          numberCapabilities: this.currentConversation.provider_number.capabilities
        };
        this.$conveyInstance.transcribeCall(params)
          .onReady((meta) => {
            if (!this.isVoiceSDKLoaded) {
              this.$store.commit("user/setIsVoiceSDKLoaded", true);
            }
          })
          .onTranscribe((data) => {})
          .onLiveAudio((data, dualChannel, live) => {
            this.hasLiveAudio = live;
            if (this.audioNode && this.playingAudio) {
              this.audioNode.port.postMessage({
                message: "audioData",
                audioData: data,
              });
            }
          })
          .onKeyword((data, isNew) => {
            this.$store.commit("keywords/addKeyword", data);
            EventBus.$emit("update-conversation-keyword", {
              conversationId: this.conversationId,
              data,
              isNew,
            });
          })
          .onSummary((data, isNew) => {
            EventBus.$emit("update-conversation-summary", {
              conversationId: this.conversationId,
              data,
              isNew,
            });
          })
          .onSentimentScore((data, isNew) => {
            console.log("[onSentimentScore]: ", data);
            EventBus.$emit("update-sentiment-score", {
              conversationId: this.conversationId,
              data,
              isNew,
            });
          })
          .onError((err) => {
            console.log("[TranscribeSDK:onError]: ", err);
            if (!this.isVoiceSDKLoaded) {
              this.$store.commit("user/setIsVoiceSDKLoaded", false);
            }
          });
        
        if (this.liveAudioCapability) {
          setTimeout(() => {
            this.appendLiveButton();
          }, 3000)

          const containerID = `voice-sdk-container-${this.conversationId}`;
          const containerElement = document.getElementById(containerID);
          containerElement.addEventListener("click", async (evt) => {
            if (this.audioNode) {
              return;
            }

            if (!this.hasLiveAudio) {
              return;
            }

            console.log("[TranscribeSDK] Initialize the audioContext: ");
            try {
              this.audioContext = new AudioContext({ sampleRate: 8000 });
              await this.audioContext.audioWorklet
                .addModule("https://convey-embed-scriptlets.s3.us-east-2.amazonaws.com/AudioProcessor.js")
                .catch((e) => {
                  console.error(e);
                  console.trace();
                });

              this.audioNode = new AudioWorkletNode(this.audioContext, "audio-processor");

              this.gainNode = this.audioContext.createGain();
              this.gainNode.gain.value = this.volume;

              this.audioNode.connect(this.gainNode);
              this.gainNode.connect(this.audioContext.destination)

              setTimeout(() => {
                if (this.liveButton) {
                  if (!this.playingAudio) {
                    this.liveButton.classList.remove("playing");
                    if (this.audioContext) {
                      this.audioContext.suspend();
                    }
                  } else {
                    this.liveButton.classList.add("playing");
                    if (this.audioContext) {
                      this.audioContext.resume();
                    }
                  }
                }
              }, 100);
            } catch (err) {
              console.log("[TranscribeSDK:initAudioContext] ERR: ", err);
            }
          });
        }
      });
    }
  },
  methods: {
    appendLiveButton() {
      const containerID = `voice-sdk-container-${this.conversationId}`;
      const containerElement = document.getElementById(containerID);
      if (containerElement) {
        const actionContainer = containerElement.querySelector(".ConveyEmbed--ActionsContainer >.ConveyEmbed--FlexCenterRow >.ConveyEmbed--FlexCenterRow:first-child");
        if (actionContainer) {
          this.liveButton = document.createElement("button");
          this.liveButton.className = "voice-sdk-live-audio-button btn btn-success btn-rounded-pill btn-sm";
          const iconElement = document.createElement("div");
          iconElement.className = "voice-sdk-live-audio-button--icon";
          const textNode = document.createTextNode("LIVE");
          this.liveButton.appendChild(iconElement);
          this.liveButton.appendChild(textNode);
          this.liveButton.onclick = this.playLiveButton;
          if (!this.hasLiveAudio) {
            this.liveButton.disabled = true;
          }
          actionContainer.appendChild(this.liveButton);
        }
      }
    },
    playLiveButton($event) {
      this.playingAudio = !this.playingAudio;
      if (this.liveButton) {
        if (!this.playingAudio) {
          this.liveButton.classList.remove("playing");
          if (this.audioContext) {
            this.audioContext.suspend();
          }
        } else {
          this.liveButton.classList.add("playing");
          if (this.audioContext) {
            this.audioContext.resume();
          }
        }
      }
    },
  },
  watch: {
    volume(volume) {
      if (this.gainNode) {
        this.gainNode.gain.value = Math.max(0, Math.min(volume, 1));
      }
    },
    currentConversation: {
      handler(currentConversation) {
        if (currentConversation.status !== 'active' && this.liveAudioCapability && this.liveButton) {
          this.hasLiveAudio = false;
          this.liveButton.remove();
          this.liveButton = null;
          if (this.audioNode) {
            this.audioNode.disconnect();
            this.audioNode = null;
          }
          if (this.audioContext) {
            this.audioContext.suspend();
            this.audioContext.close();
            this.audioContext = null;
          }
        }
      },
      immediate: true,
    },
    hasLiveAudio(hasLiveAudio) {
      if (this.liveButton) {
        this.liveButton.disabled = !hasLiveAudio;
      }
    },
  },
  beforeDestroy() {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    if (this.audioNode) {
      this.audioNode.disconnect();
      this.audioNode = null;
    }
    if (this.$conveyInstance && this.$conveyInstance.transcribe) {
      this.$conveyInstance.transcribe.destroy();
    }
  }
};
</script>

<style lang="scss">
</style>
